import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiClock, FiUser, FiHash, FiCheck, FiX, FiLayers, FiCalendar } from 'react-icons/fi';
import SignatureModal from './SignatureModal';

function playChime(isFinal) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);

    if (isFinal) {
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.setValueAtTime(1200, ctx.currentTime + 0.15);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } else {
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch (e) { /* Web Audio not supported */ }
}

export default function TimeConfirmModal({ user, operations = [], onSubmit, onClose, preSelectedOperationId, loggedHours = 0, teamMembers = [] }) {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [isFinal, setIsFinal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [showSignature, setShowSignature] = useState(false);
  const [workDate, setWorkDate] = useState(new Date().toISOString().split('T')[0]);
  // "Confirm as" — defaults to the caller. When they have team members under
  // them, they can pick a different user to log time against.
  const [confirmAsId, setConfirmAsId] = useState(user?.id || 0);
  const confirmAsOptions = [
    { id: user?.id || 0, name: `${user?.name || 'Myself'} (me)`, employee_id: user?.employee_id || '' },
    ...teamMembers.filter(m => m.id !== user?.id),
  ];
  const confirmAs = confirmAsOptions.find(o => o.id === confirmAsId) || confirmAsOptions[0];

  // Operation is required - default to pre-selected or first non-confirmed operation
  const openOps = operations.filter(op => op.status !== 'Complete');
  const defaultOpId = preSelectedOperationId || (openOps.length > 0 ? openOps[0].id : (operations.length > 0 ? operations[0].id : 0));
  const [selectedOpId, setSelectedOpId] = useState(defaultOpId);
  const selectedOp = operations.find(op => op.id === selectedOpId) || null;

  // Multi-day enforcement: check if enough hours have been logged before allowing final
  const totalLogged = loggedHours + hours + minutes / 60;
  const estimatedHours = selectedOp?.estimated_hours ? parseFloat(selectedOp.estimated_hours) : 0;
  const showMultiDayBanner = estimatedHours > 0 && totalLogged < estimatedHours - 0.01;

  const handleFinalChange = (e) => {
    setIsFinal(e.target.checked);
  };

  // Called after signature is captured (or directly for non-final submissions)
  const submitWithSignature = async (workerSignature) => {
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({ hours, minutes, is_final: isFinal, operation_id: selectedOp.id, worker_signature: workerSignature || null, work_date: workDate, on_behalf_of_user_id: confirmAsId && confirmAsId !== user?.id ? confirmAsId : null });
      playChime(isFinal);
      setSuccess(isFinal ? 'final' : 'confirmed');
    } catch (e) {
      setError(e.message || 'Failed to submit time confirmation. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (hours === 0 && minutes === 0) return;
    if (!selectedOp) { setError('Please select an operation'); return; }
    if (isFinal) {
      // Show signature pad first
      setShowSignature(true);
    } else {
      await submitWithSignature(null);
    }
  };

  const handleSign = async (signatureDataUrl) => {
    setShowSignature(false);
    await submitWithSignature(signatureDataUrl);
  };

  const handleSuccessClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(handleSuccessClose, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, handleSuccessClose]);

  const selectStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
    background: 'var(--bg-input)', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', outline: 'none',
    appearance: 'none', cursor: 'pointer'
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
        }}
        onClick={handleSuccessClose}
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          onClick={e => e.stopPropagation()}
          style={{
            background: 'var(--bg-card)', borderRadius: 20, padding: '40px 32px', width: '90%', maxWidth: 360,
            textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.1 }}
            style={{
              width: success === 'final' ? 80 : 64,
              height: success === 'final' ? 80 : 64,
              borderRadius: '50%',
              background: success === 'final' ? 'linear-gradient(135deg, #10b981, #059669)' : '#10b981',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: success === 'final' ? '0 8px 32px rgba(16,185,129,0.4)' : '0 4px 16px rgba(16,185,129,0.3)'
            }}
          >
            <FiCheck size={success === 'final' ? 40 : 32} color="#fff" strokeWidth={3} />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{
              margin: '0 0 8px', fontSize: success === 'final' ? 22 : 18, fontWeight: 700,
              color: 'var(--text-primary)'
            }}
          >
            {success === 'final' ? `Operation ${selectedOp?.operation_number || ''} Completed!` : 'Time Completed!'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}
          >
            {hours > 0 ? `${hours}h ` : ''}{minutes > 0 ? `${minutes}m` : ''} logged
            {success === 'final' ? ' as final confirmation' : ''}
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      {showSignature && (
        <SignatureModal
          title="Worker Signature Required"
          subtitle="Please sign to confirm final time completion. This signature will appear on the shop paper."
          name={confirmAs?.name?.replace(/ \(me\)$/, '') || user?.name}
          onSign={handleSign}
          onCancel={() => setShowSignature(false)}
        />
      )}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          onClick={e => e.stopPropagation()}
          style={{
            background: 'var(--bg-card)', borderRadius: 20, padding: '28px', width: '90%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiClock size={20} color="#2563eb" /> Time Confirmation
            </h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
              <FiX size={20} />
            </button>
          </div>

          {/* Confirm-as picker (only when the user supervises a team) */}
          {teamMembers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                <FiUser size={12} /> Confirming time as
              </label>
              <div style={{ position: 'relative' }}>
                <select value={confirmAsId} onChange={e => setConfirmAsId(parseInt(e.target.value))} style={selectStyle}>
                  {confirmAsOptions.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}{o.employee_id ? ` — ${o.employee_id}` : ''}
                    </option>
                  ))}
                </select>
                <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>&#9662;</div>
              </div>
            </div>
          )}

          {/* User Info (read-only) */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20,
            padding: '14px', borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)'
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiUser size={10} /> Full Name
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{confirmAs?.name?.replace(/ \(me\)$/, '') || ''}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiHash size={10} /> Employee ID
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{confirmAs?.employee_id || ''}</div>
            </div>
          </div>

          {/* Operation Selection (required) */}
          {operations.length > 0 ? (
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                <FiLayers size={12} /> Operation <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <select value={selectedOpId} onChange={e => setSelectedOpId(parseInt(e.target.value))} style={selectStyle}>
                  {operations.map(op => (
                    <option key={op.id} value={op.id} disabled={op.status === 'Complete'}>
                      {op.operation_number} — {op.title}{op.status === 'Complete' ? ' (Completed)' : ''}
                    </option>
                  ))}
                </select>
                <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>&#9662;</div>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 20, padding: '12px 14px', borderRadius: 10, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13 }}>
              No operations found. Please add operations to the work order before confirming time.
            </div>
          )}

          {/* Work Date */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
              <FiCalendar size={12} /> Work Date
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="date"
                value={workDate}
                onChange={e => setWorkDate(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
                  background: 'var(--bg-input)', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Time Selection */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Hours</label>
              <div style={{ position: 'relative' }}>
                <select value={hours} onChange={e => setHours(parseInt(e.target.value))} style={selectStyle}>
                  {Array.from({ length: 25 }, (_, i) => (
                    <option key={i} value={i}>{i} {i === 1 ? 'hour' : 'hours'}</option>
                  ))}
                </select>
                <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>&#9662;</div>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Minutes</label>
              <div style={{ position: 'relative' }}>
                <select value={minutes} onChange={e => setMinutes(parseInt(e.target.value))} style={selectStyle}>
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                </select>
                <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>&#9662;</div>
              </div>
            </div>
          </div>

          {/* Final Confirmation Checkbox */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '14px',
            borderRadius: 12, border: `2px solid ${isFinal ? '#10b981' : 'var(--border)'}`,
            background: isFinal ? 'rgba(16,185,129,0.05)' : 'var(--bg-card)',
            cursor: 'pointer', marginBottom: (isFinal || showMultiDayBanner) ? 12 : 24, transition: 'all 0.2s'
          }}>
            <input type="checkbox" checked={isFinal} onChange={handleFinalChange}
              style={{ width: 18, height: 18, accentColor: '#10b981', cursor: 'pointer' }}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: isFinal ? '#059669' : 'var(--text-primary)' }}>
                Final Time Confirmation
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {selectedOp ? `This will mark operation ${selectedOp.operation_number} as completed` : 'Select an operation first'}
              </div>
            </div>
          </label>

          {/* Multi-day enforcement banner */}
          {showMultiDayBanner && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10,
              background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.25)',
              marginBottom: 24, fontSize: 12, color: '#d97706', fontWeight: 500
            }}>
              ⚠️ {totalLogged.toFixed(1)}h of {estimatedHours.toFixed(1)}h estimated logged. Submit remaining days before marking final.
            </div>
          )}

          {/* Signature notice when final is checked */}
          {isFinal && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10,
              background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)',
              marginBottom: 24, fontSize: 12, color: '#059669', fontWeight: 500
            }}>
              <FiCheck size={13} />
              You will be asked to sign after clicking Complete. Signature is required and will appear on the shop paper.
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 16,
              background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13
            }}>{error}</div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{
              flex: 1, padding: '12px', borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}>Cancel</button>
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={submitting || (hours === 0 && minutes === 0)}
              style={{
                flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                background: (hours === 0 && minutes === 0) ? 'var(--border)' : (isFinal ? '#10b981' : '#2563eb'),
                color: (hours === 0 && minutes === 0) ? 'var(--text-muted)' : '#fff',
                fontSize: 14, fontWeight: 600, cursor: (hours === 0 && minutes === 0) ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Submitting...' : (isFinal ? 'Complete Operation' : 'Confirm Time')}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
